import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Button from "../../../Button/Button";
import styles from "./ConfirmAndPay.module.scss";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";
import { createPayment } from "@/api/paymentAPI";
import { useLoyaltyStore } from "@/store/useLoyaltyStore";
import { paymentInstrumentHash } from "@/helpers/paymentHash";
import { clearTipDraft } from "@/store/useTipDraftStore";
import useOnClickFunctions from "../../helpers/onClickFunctions";

function ConfirmAndPay() {
  const { t } = useTranslation();
  const { restaurantId = "", tableId = "" } = useParams<{
    restaurantId: string;
    tableId: string;
  }>();
  const {
    paymentMethod,
    blikCode,
    splitMethod,
    selectedAmount,
    selectedDishIds,
    tipAmount,
  } = useVenuesDataStore();
  const { handleNavigate } = useOnClickFunctions();
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    if (paymentMethod === "Blik" && blikCode.length !== 6) {
      handleNavigate(
        `/restaurants/${restaurantId}/tables/${tableId}/payment-error`,
      );
      return;
    }

    if (!splitMethod) return;

    setLoading(true);
    try {
      // The tip travels in its own field now, so it is recorded as a tip and
      // booked against whoever is serving the table. It must therefore leave
      // `amount`: the backend charges the two added together.
      const tipGrosze = Math.round(tipAmount * 100);

      await createPayment(tableId, {
        method: splitMethod,
        ...(splitMethod === "items" && { orderDishIds: selectedDishIds }),
        ...(splitMethod === "custom" && {
          amount: Math.round(selectedAmount * 100),
        }),
        ...(tipGrosze > 0 && { tipAmount: tipGrosze }),
      });
      clearTipDraft();
      useLoyaltyStore
        .getState()
        .registerPayment(
          Math.round(selectedAmount * 100 + tipAmount * 100),
          paymentInstrumentHash(paymentMethod, blikCode),
        );
      handleNavigate(`/restaurants/${restaurantId}/summary`);
    } catch {
      handleNavigate(
        `/restaurants/${restaurantId}/tables/${tableId}/payment-error`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.paymentButtonsGroupWrapper}>
      <Button
        onClick={handlePay}
        label={
          loading
            ? t("transparentDrawer.button.pay") + "..."
            : t("transparentDrawer.button.pay")
        }
        variant="contained"
        color="primary"
        maxWidth
        mobile={true}
      />
      <div className={`${styles.footerContainer}`}>
        <div className={styles.footerTitleContainer}>
          <p className={`body-small ${styles.footerTitleText}`}>
            {t("homePage.footer.title.firstPart")}
          </p>
          <p className={`body-small-logo ${styles.footerTitleText}`}>
            {t("homePage.footer.title.secondPart")}
          </p>
        </div>

        <p className={`body-small ${styles.footerDescription}`}>
          {t("homePage.footer.description.firstPart")}
          <mark className="mark-underlined">
            {t("homePage.footer.description.secondPart")}
          </mark>
          {t("homePage.footer.description.thirdPart")}
          <mark className="mark-underlined">
            {t("homePage.footer.description.fourthPart")}
          </mark>
          {t("homePage.footer.description.fifthPart")}
        </p>
      </div>
    </div>
  );
}

export default ConfirmAndPay;
